// Pure derivation behind the resources calendar — no React, no GraphQL, so it stays testable.

import { CEILINGS } from './constants';

export const FILL_STATES = {
    AVAILABLE: 'available',
    NEAR: 'near',
    FULL: 'full',
    UNCONFIGURED: 'unconfigured',
};

export const NEAR_CAP_PERCENT = 85;

export const fillState = (percent) => {
    if (percent >= 100) return FILL_STATES.FULL;
    if (percent >= NEAR_CAP_PERCENT) return FILL_STATES.NEAR;
    return FILL_STATES.AVAILABLE;
};

export const loadKey = (resourceId, deliveryDay) => `${resourceId}|${deliveryDay}`;

export const indexByResourceDay = (rows = []) =>
    Object.fromEntries((rows || []).map((row) => [loadKey(row.resource_id, row.delivery_day), row]));

// The view calls pieces `used_pieces` while the ceiling is `items`. Hasura sends numerics as strings.
export const normalizeLoad = (row) => ({
    cubes: Number(row?.used_cubes ?? 0) || 0,
    weight: Number(row?.used_weight ?? 0) || 0,
    stops: Number(row?.used_stops ?? 0) || 0,
    items: Number(row?.used_pieces ?? 0) || 0,
    orders: Number(row?.committed_orders ?? 0) || 0,
    // Live orders not yet complete, so nothing on them can invoice.
    incomplete: Number(row?.incomplete_orders ?? 0) || 0,
});

// Hasura sends the views' numerics as strings, and a column the row does not carry must read 0
// rather than NaN.
const orderCount = (value) => Number(value ?? 0) || 0;

const ceilingLimit = (resource, ceiling) => {
    const raw = resource?.[ceiling.column];
    const limit = Number(raw ?? 0) || 0;

    return limit > 0 ? limit : null;
};

// A null ceiling reads 0 rather than inventing a denominator.
// Rounded before fillState sees it, so the number shown and the state always agree: 99.5% reads
// "100%" and Full, never "100%" and Near cap.
export const percentOf = (used, limit) => {
    if (!limit) {
        return 0;
    }

    return Math.round(((used || 0) / limit) * 100);
};

// The fullest configured ceiling, not a stored preference. There is no single capacity dimension:
// every ceiling is checked on its own, so nominating one to display meant the bar could read low
// while a different dimension was already over. Showing whichever is closest to its limit is always
// the one that matters, and needs no configuration.
//
// UNCONFIGURED rather than a 0% bar — a resource with no ceilings set has nothing to measure.
export const resourceFill = ({ resource, load }) => {
    const normalized = normalizeLoad(load);

    const configured = CEILINGS.map((ceiling) => {
        const limit = ceilingLimit(resource, ceiling);

        return limit ? { ceiling, limit, used: normalized[ceiling.value] } : null;
    }).filter(Boolean);

    if (configured.length === 0) {
        return { ceiling: null, used: null, limit: null, percent: null, state: FILL_STATES.UNCONFIGURED };
    }

    const fullest = configured
        .map((entry) => ({ ...entry, percent: percentOf(entry.used, entry.limit) }))
        .reduce((highest, entry) => (entry.percent > highest.percent ? entry : highest));

    return { ...fullest, state: fillState(fullest.percent) };
};

// INCOMPLETE is work that ran and never closed out; NONE is a resource-day with nothing on it;
// PROCESSING is for approved/processing orders (in the payment pipeline but not yet complete).
//
// UNINVOICED is work that finished with no bill raised. It used to fall through to UNPAID, which
// read wrong: nobody is late paying a bill that never went out.
export const BILLING_STATES = {
    PAID: 'paid',
    UNPAID: 'unpaid',
    UNINVOICED: 'uninvoiced',
    INCOMPLETE: 'incomplete',
    PROCESSING: 'processing',
    NONE: 'none',
};

// Worst status wins. A card shows one state for a resource-day that can hold several at once, so
// it is read bottom-up against how far each is from money in the bank, worst first:
//
//     orders never completed > not invoiced > invoiced > approved > all paid
//
// INCOMPLETE leads because an order that never completed cannot be invoiced at all, let alone
// chased. UNINVOICED beats UNPAID because a bill that never went out is a step further back than
// one that did — this reverses the earlier ranking, which read a sent-but-unpaid bill as more
// urgent and so hid uninvoiced work behind it. PROCESSING is last of the open states: it is already
// moving and needs nobody to do anything.
//
// Checked against the counts rather than `summary.notPaid`, which folds the payment pipeline in
// with the unpaid bills and cannot tell the two states apart.
export const resourceBillingState = (row, load) => {
    if (normalizeLoad(load).incomplete > 0) {
        return BILLING_STATES.INCOMPLETE;
    }

    const summary = billingSummary(row);

    if (!summary) {
        return BILLING_STATES.NONE;
    }

    if (summary.notInvoiced > 0) {
        return BILLING_STATES.UNINVOICED;
    }

    if (orderCount(row.draft_orders) + orderCount(row.unpaid_orders) > 0) {
        return BILLING_STATES.UNPAID;
    }

    if (orderCount(row.approved_orders) + orderCount(row.processing_orders) > 0) {
        return BILLING_STATES.PROCESSING;
    }

    if (summary.denominator <= 0) {
        return BILLING_STATES.NONE;
    }

    // Every open bucket is empty, so the row should be settled. When the paid count still falls
    // short of the denominator the difference is unaccounted for, and reading it as outstanding is
    // the safer of the two wrong answers.
    return summary.closed ? BILLING_STATES.PAID : BILLING_STATES.UNPAID;
};

// A past day still uninvoiced this long after the fact has missed the weekly billing run and is
// exactly the gap the calendar exists to surface. Anything more recent is normal and stays quiet.
export const UNINVOICED_REVIEW_DAYS = 14;

// Both arguments are YYYY-MM-DD calendar dates in the client's zone already, so they subtract as
// UTC midnights — no offset math, and no DST shift to round away.
export const daysBetweenDayKeys = (from, to) => {
    if (!from || !to) {
        return null;
    }

    return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);
};

// Read off the entry's own counts, not the state its card collapsed to. Under worst-status-wins a
// resource carrying both an uninvoiced order and an unpaid one displays UNINVOICED, and UNINVOICED
// alone stays quiet until it ages — so reading the collapsed state would drop that unpaid bill off
// the badge for a fortnight. The card answers "what is the worst thing here"; this answers "is
// there anything here at all", and they are not the same question.
const entryNeedsReview = ({ billingRow, load } = {}, stale) => {
    if (normalizeLoad(load).incomplete > 0) {
        return true;
    }

    const open =
        orderCount(billingRow?.draft_orders) +
        orderCount(billingRow?.unpaid_orders) +
        orderCount(billingRow?.approved_orders) +
        orderCount(billingRow?.processing_orders);

    return open > 0 || (stale && orderCount(billingRow?.uninvoiced_orders) > 0);
};

// Read off every entry, not the handful a cell can show. Uninvoiced work is the one thing that is a
// flag only once it has aged — without a date the day cannot be aged, so it does not flag.
export const dayNeedsReview = ({ entries = [], dateKey, todayKey } = {}) => {
    const age = daysBetweenDayKeys(dateKey, todayKey);
    const stale = age != null && age >= UNINVOICED_REVIEW_DAYS;

    return entries.some((entry) => entryNeedsReview(entry, stale));
};

// The four the day card shows, collapsed from the six the view counts. Grouped here rather than in
// the SQL view on purpose: the part-2 migration keeps per-status counts precisely so which statuses
// belong together stays a product decision, and both groupings have already moved once.
//
// `incomplete` is counted under "Not invoiced" rather than given a fifth bucket. It comes from the
// load view, not the billing one, but it belongs there on the operator's reading: an order that
// never closed out cannot have been billed, so it is money that has not gone out the door.
export const BILLING_BUCKETS = [
    { key: 'uninvoiced', label: 'Not invoiced', fields: ['uninvoiced_orders'], withIncomplete: true },
    { key: 'unpaid', label: 'Invoiced', fields: ['draft_orders', 'unpaid_orders'] },
    { key: 'processing', label: 'Approved', fields: ['approved_orders', 'processing_orders'] },
    { key: 'paid', label: 'Paid', fields: ['paid_orders'] },
];

// Always four entries, zeros included: the card is a fixed 2x2 grid, and dropping an empty bucket
// would reshape it day to day.
export const dayBillingBuckets = (row, incomplete = 0) =>
    BILLING_BUCKETS.map(({ key, label, fields, withIncomplete }) => ({
        key,
        label,
        count:
            fields.reduce((total, field) => total + orderCount(row?.[field]), 0) +
            (withIncomplete ? orderCount(incomplete) : 0),
    }));

// isPrimary marks the ceiling the bar is currently showing — the fullest — so the side dialog can
// emphasise the same row the calendar card leads with.
export const ceilingBreakdown = ({ resource, load }) => {
    const normalized = normalizeLoad(load);
    const shown = resourceFill({ resource, load })?.ceiling?.value;

    return CEILINGS.map((ceiling) => {
        const limit = ceilingLimit(resource, ceiling);

        return {
            ceiling,
            used: normalized[ceiling.value],
            limit,
            percent: limit ? percentOf(normalized[ceiling.value], limit) : null,
            isPrimary: shown === ceiling.value,
        };
    });
};

// timeframe_start / timeframe_end are integer hours, 0-23.
export const formatHour = (hour) => {
    if (hour == null) {
        return null;
    }

    const value = Number(hour);
    const suffix = value >= 12 ? 'pm' : 'am';
    const display = value % 12 === 0 ? 12 : value % 12;

    return `${display}${suffix}`;
};

export const formatTimeframe = (start, end) => {
    if (start == null && end == null) {
        return null;
    }

    return [formatHour(start) ?? '?', formatHour(end) ?? '?'].join(' – ');
};

// UNCONFIGURED rows have no percent to sort on, so they fall to the bottom, alphabetically.
export const sortByFill = (entries = []) =>
    [...entries].sort((a, b) => {
        const aPercent = a.fill?.percent;
        const bPercent = b.fill?.percent;

        if (aPercent == null && bPercent == null) {
            return (a.resource?.name || '').localeCompare(b.resource?.name || '');
        }

        if (aPercent == null) return 1;
        if (bPercent == null) return -1;

        return bPercent - aPercent;
    });

// One bar per ceiling across the whole day — what the cell shows instead of a card per resource.
//
// A resource that set no limit for a measure is excluded from *both* sides of that ceiling. Include
// its load and not its capacity and a bar runs past 100% on capacity nobody declared: a truck
// carrying 400 cu ft with no cubes ceiling would be measured against its neighbours' denominators.
//
// `contributors` is how many resources did declare one, which is what lets the card say "3 of 7
// trucks" rather than implying the bar covers the whole fleet.
export const dayCeilingRollup = (entries = []) =>
    CEILINGS.map((ceiling) => {
        const { used, limit, contributors } = entries.reduce(
            (acc, { resource, load }) => {
                const cap = ceilingLimit(resource, ceiling);

                if (!cap) {
                    return acc;
                }

                return {
                    used: acc.used + normalizeLoad(load)[ceiling.value],
                    limit: acc.limit + cap,
                    contributors: acc.contributors + 1,
                };
            },
            { used: 0, limit: 0, contributors: 0 }
        );

        // No contributors is "nobody set this ceiling", not "0% full" — the card mutes it rather
        // than drawing an empty bar, and it stays out of the day percentage entirely.
        if (!contributors) {
            return { ceiling, used: 0, limit: 0, contributors: 0, percent: null, state: FILL_STATES.UNCONFIGURED };
        }

        const percent = percentOf(used, limit);

        return { ceiling, used, limit, contributors, percent, state: fillState(percent) };
    });

// The day badge is the fullest bar. It used to average each resource's own fullest ceiling, which
// with the bars now on the card put a second, differently-computed number directly above them.
export const rollupPercent = (rollup = []) =>
    rollup.reduce((highest, { percent }) => (percent != null && percent > highest ? percent : highest), 0);

export const rollupMeasured = (rollup = []) => rollup.some(({ percent }) => percent != null);

// `routes` counts resource-days actually carrying orders. Capacity is dayCeilingRollup's job.
export const dayAggregate = (entries = []) =>
    entries.reduce(
        (acc, { load }) => {
            const normalized = normalizeLoad(load);

            return {
                routes: acc.routes + (normalized.orders > 0 ? 1 : 0),
                stops: acc.stops + normalized.stops,
                pieces: acc.pieces + normalized.items,
                orders: acc.orders + normalized.orders,
                incomplete: acc.incomplete + normalized.incomplete,
                resources: acc.resources + 1,
            };
        },
        { routes: 0, stops: 0, pieces: 0, orders: 0, incomplete: 0, resources: 0 }
    );

// Per the billing view's migration header: APPROVED is not paid and shipper invoices are born DRAFT,
// so "not paid" spans four statuses. Cancelled is out of the denominator.
//
// `incomplete` blocks `closed`: orders still running will generate invoices that do not exist yet.
// The shape both callers share. A day with no billing row still renders the tiles, so the zero case
// is built from the same object rather than hand-written beside them.
export const emptyBillingSummary = (incomplete = 0) => ({
    paid: 0,
    notPaid: 0,
    notInvoiced: 0,
    cancelled: 0,
    incomplete,
    denominator: 0,
    closed: false,
});

export const billingSummary = (row, incomplete = 0) => {
    if (!row) {
        return null;
    }

    const num = (value) => Number(value ?? 0) || 0;
    const cancelled = num(row.cancelled_orders);
    const paid = num(row.paid_orders);
    const denominator = num(row.billable_orders) - cancelled;

    return {
        ...emptyBillingSummary(incomplete),
        paid,
        notPaid: num(row.draft_orders) + num(row.unpaid_orders) + num(row.approved_orders) + num(row.processing_orders),
        notInvoiced: num(row.uninvoiced_orders),
        cancelled,
        denominator,
        closed: denominator > 0 && denominator === paid && incomplete === 0,
    };
};

// An allowlist, not "sum every key on the row": a new view column is ignored by default rather than
// quietly landing on 0 for anything non-numeric.
const SUMMABLE_BILLING_FIELDS = [
    'billable_orders',
    'invoiced_orders',
    'cancelled_orders',
    // Every per-status count the four buckets group.
    ...BILLING_BUCKETS.flatMap(({ fields }) => fields),
];

// Returns the summed row rather than a summary so callers can read individual statuses off it.
const sumBillingRows = (rows = []) => {
    if (!rows.length) {
        return null;
    }

    return rows.reduce((acc, row) => {
        SUMMABLE_BILLING_FIELDS.forEach((key) => {
            acc[key] = (acc[key] || 0) + (Number(row[key] ?? 0) || 0);
        });

        return acc;
    }, {});
};

// A cell's `dateKey` is already the views' `delivery_day`, so no timezone conversion happens here.
export const buildCalendarDays = ({
    cells = [],
    coreDays = {},
    dayToResource = {},
    resourceById = {},
    loadRows,
    billingRows,
    todayKey,
}) => {
    const loadIndex = indexByResourceDay(loadRows);
    const billingIndex = indexByResourceDay(billingRows);

    const days = cells.map(({ dateKey, isCurrentMonth }) => {
        const scheduled = dayToResource?.[dateKey] || {};

        const entries = sortByFill(
            Object.entries(scheduled)
                .map(([resourceId, { dayState }]) => {
                    const resource = resourceById[resourceId];
                    if (!resource) {
                        return null;
                    }

                    const load = loadIndex[loadKey(resourceId, dateKey)];
                    const billingRow = billingIndex[loadKey(resourceId, dateKey)];

                    return {
                        resource,
                        // Resolved once by the store's dayToResource, not per cell — see R13.
                        // `accepting` is read off the same object rather than carried beside it,
                        // so the flag the card mutes on and the state the lock toggle writes from
                        // cannot drift apart.
                        dayState,
                        accepting: dayState?.accepting ?? true,
                        load,
                        fill: resourceFill({ resource, load }),
                        billingRow,
                        billingState: resourceBillingState(billingRow, load),
                    };
                })
                .filter(Boolean)
        );

        const aggregate = dayAggregate(entries);
        const ceilingRollup = dayCeilingRollup(entries);

        // One billing row per resource-day, so a day's billing is the sum of its resources'.
        const billingRowsForDay = entries.map(({ billingRow }) => billingRow).filter(Boolean);
        const summedBillingRow = sumBillingRows(billingRowsForDay);

        return {
            dateKey,
            isCurrentMonth,
            // The instant-based day `scheduleResource` and the resource dialog expect.
            day: coreDays[dateKey],
            entries,
            aggregate,
            ceilingRollup,
            percent: rollupPercent(ceilingRollup),
            measured: rollupMeasured(ceilingRollup),
            needsReview: dayNeedsReview({ entries, dateKey, todayKey }),
            // Null with no billing row, so monthStats' "days billed" denominator is unchanged.
            billing: summedBillingRow ? billingSummary(summedBillingRow, aggregate.incomplete) : null,
            buckets: dayBillingBuckets(summedBillingRow, aggregate.incomplete),
        };
    });

    return {
        daysByKey: Object.fromEntries(days.map((day) => [day.dateKey, day])),
        monthDays: days.filter(({ isCurrentMonth }) => isCurrentMonth),
    };
};

export const monthStats = (days = []) => {
    // day.percent, not a second derivation off the aggregate: the KPI is the average of the numbers
    // the cells show, so the two cannot disagree.
    const measured = days.filter((day) => day.measured);
    const totalPercent = measured.reduce((sum, day) => sum + (day.percent || 0), 0);

    // Denominator is days with any billing at all — a day with nothing invoiced cannot close.
    const billed = days.filter((day) => day.billing);
    const closed = billed.filter((day) => day.billing.closed).length;

    return {
        // Resources, not routes — the same number the day cells show.
        resources: days.reduce((sum, day) => sum + day.aggregate.resources, 0),
        stops: days.reduce((sum, day) => sum + day.aggregate.stops, 0),
        pieces: days.reduce((sum, day) => sum + day.aggregate.pieces, 0),
        avgCapacity: measured.length ? Math.round(totalPercent / measured.length) : 0,
        daysClosed: closed,
        daysBilled: billed.length,
        daysClosedRatio: `${closed} / ${billed.length}`,
    };
};
